import EpiApiClient from "@/services/serviceClients/epiApiClient";
import { 
  ACCOUNT_USER_DETAILS_API_PATH,
  USER_SETTINGS_API_PATH,
  ACCOUNT_TRENDS_API_PATH,
  USER_ADD_FAVORITE_API_PATH,
  USER_REMOVE_FAVORITE_API_PATH,
  CUSTOMER_LOGO_API_PATH,
  CUSTOMER_LOGO_PROXY_API_PATH,
  UPLOAD_CUSTOMER_LOGO_API_PATH,
  USER_DETAILS_API_PATH,
  USER_ROLES_API_PATH,
  ACCOUNT_CHANGE_PASSWORD_API_PATH,
  ACCOUNT_UPDATE_API_PATH,
  ACCOUNT_UPLOAD_PROFILE_PICTURE_API_PATH,
  ACCOUNT_SET_HOME_BRANCH_API_PATH,
  ACCOUNT_ADD_USER_BRANCH_API_PATH,
  ACCOUNT_REMOVE_USER_BRANCH_API_PATH,
  ACCOUNT_CLEAR_USER_DETAIL_CACHE_API_PATH,
  ACCOUNT_COMPANY_USERS_API_PATH,
  ACCOUNT_INVITE_USER_API_PATH,
  ACCOUNT_USER_EMAIL_AVAILABLE_API_PATH,
  ACCOUNT_UPDATE_COMPANY_USER_API_PATH,
  ACCOUNT_SET_USER_HOME_BRANCH_API_PATH,
  USER_NOTIFICATION_PREFERENCES_API_PATH,
  DELETE_USER_NOTIFICATION_PREFERENCES_API_PATH
} from "@/config/constants"

class accountService {
  static async fetchUserDetails() {
    try {
      const response = await EpiApiClient.get(ACCOUNT_USER_DETAILS_API_PATH);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error_description
        : "request failed";
      return { success: false, error: errorMessage };
    }
  }

  static async postSaveDefaultViewDetails(payload) {
    try {
      await EpiApiClient.post(USER_SETTINGS_API_PATH, payload);
      return { success: true };
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error_description
        : "request failed";
      return { success: false, error: errorMessage };
    }
  }

  static async fetchTrendsData() {
    try {
      const response = await EpiApiClient.get(ACCOUNT_TRENDS_API_PATH);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || error.message || "request failed";
      console.error('accountService.fetchTrendsData failed:', errorMessage, error);
      return { success: false, error: errorMessage };
    }
  }

  static async addAsFavorite(number) {
    return EpiApiClient.get(USER_ADD_FAVORITE_API_PATH, {
      params: { number }
    });
  }

  static async removeAsFavorite(number) {
    return EpiApiClient.get(USER_REMOVE_FAVORITE_API_PATH, {
      params: { number }
    });
  }

  static async setHomeBranch(branchCd) {
    try {
      const response = await EpiApiClient.get(ACCOUNT_SET_HOME_BRANCH_API_PATH, {
        params: { branchCode: branchCd }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  static async addUserBranch(branchCd, userId) {
    try {
      const response = await EpiApiClient.get(ACCOUNT_ADD_USER_BRANCH_API_PATH, {
        params: { branchCode: branchCd, userId }
      });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || error.message || "request failed";
      console.error('accountService.addUserBranch failed:', errorMessage, error);
      return { success: false, error: errorMessage };
    }
  }

  static async removeUserBranch(branchCd, userId) {
    try {
      const response = await EpiApiClient.get(ACCOUNT_REMOVE_USER_BRANCH_API_PATH, {
        params: { branchCode: branchCd, userId }
      });
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || error.message || "request failed";
      console.error('accountService.removeUserBranch failed:', errorMessage, error);
      return { success: false, error: errorMessage };
    }
  }

  static async clearUserDetailCache() {
    try {
      const response = await EpiApiClient.get(ACCOUNT_CLEAR_USER_DETAIL_CACHE_API_PATH);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || error.message || "request failed";
      console.error('accountService.clearUserDetailCache failed:', errorMessage, error);
      return { success: false, error: errorMessage };
    }
  }

  static loadUserDetails() {
    const user = JSON.parse(sessionStorage.getItem("rh_user_details"));
    return user ? user : null;
  }

  /**
   * Fetches the customer logo URL from blob storage.
   * Note: The returned URL may be blocked by CORS/Private Network Access in browsers.
   * Consider using getCustomerLogoProxyUrl instead for direct image display.
   */
  static async fetchCustomerLogo(customerCode) {
    if (!customerCode) {
      return { success: false, error: "CustomerCode is required" };
    }
    try {
      const { data } = await EpiApiClient.get(`${CUSTOMER_LOGO_API_PATH}/${customerCode}`);
      return { success: true, response: data };
    } catch (error) {
      return { success: false, error: error.message || "request failed" };
    }
  }

  /**
   * Returns the proxy URL for a customer logo to avoid CORS/Private Network Access issues.
   * Use this URL directly in <img src="..."> tags instead of the blob storage URL.
   * @param {string} customerCode - Customer code (e.g., "ABC123")
   * @returns {string} Proxy URL for the customer logo
   */
  static getCustomerLogoProxyUrl(customerCode) {
    if (!customerCode) {
      return '';
    }
    return `${CUSTOMER_LOGO_PROXY_API_PATH}/${encodeURIComponent(customerCode)}`;
  }

  static async uploadCustomerLogo(file) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await EpiApiClient.post(UPLOAD_CUSTOMER_LOGO_API_PATH, formData, {
        headers: { 'Content-Type': undefined }
      });
      return { success: true, response: data };
    } catch (error) {
      return { success: false, error: error.message || "upload failed" };
    }
  }

  static async getUserDetails() {
    try{
      const response = await EpiApiClient.get(USER_DETAILS_API_PATH);
      return { success: true, data: response.data };  
    } catch (error) {
      return { success: false, error: error.response.data.error_description };
    }
  }

  static async getRoles() {
    try{
      const response = await EpiApiClient.get(USER_ROLES_API_PATH);
      return { success: true, data: response.data };  
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || error.message || "request failed";
      console.error('accountService.getRoles failed:', errorMessage, error);
      return { success: false, error: errorMessage };
    }
  }
  
  static async updateUserDetails(model, updateScope = "profile") {
    try {
      const response = await EpiApiClient.post(ACCOUNT_UPDATE_API_PATH, model , {
        handleError: false,
        params: { updateScope }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, Message: error.response.data.Message, errorList: error.response.data };
    }
  }

  static async uploadProfilePicture(formData) {
    try {
      const response = await EpiApiClient.post(ACCOUNT_UPLOAD_PROFILE_PICTURE_API_PATH,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response.data };
    }
  }

  static async changePassword(model) {
  try {
    const response = await EpiApiClient.post(
      ACCOUNT_CHANGE_PASSWORD_API_PATH,
      model,
      { handleError: false }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.ModelState || "request failed"
    };
  }
}

  static async getUsersForCompany(filter) {
    try {
      const response = await EpiApiClient.post(ACCOUNT_COMPANY_USERS_API_PATH, filter);
      return { success: true, data: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.ModelState || error.message || "request failed";
      console.error('accountService.getUsersForCompany failed:', errorMessage, error);
      return { success: false, error: errorMessage };
    }
  }

  static async inviteUser(model) {
    try {
      const response = await EpiApiClient.post(ACCOUNT_INVITE_USER_API_PATH, model, {
        handleError: false
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.ModelState || error.message };
    }
  }

  static async getUserEmailAvailable(email) {
    try {
      const response = await EpiApiClient.get(ACCOUNT_USER_EMAIL_AVAILABLE_API_PATH, {
        params: { email }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error_description || error.message };
    }
  }

  static async updateCompanyUser(model, isAddNewUser = false) {
    try {
      const response = await EpiApiClient.post(
        ACCOUNT_UPDATE_COMPANY_USER_API_PATH,
        model,
        {
          handleError: false,
          params: { isAddNewUser }
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.ModelState || errorData?.error_description || errorData || error.message || "request failed";
      return { success: false, error: errorMessage, data: {} };
    }
  }

  static async setUserHomeBranch(branchCd, userId) {
    try {
      const response = await EpiApiClient.get(ACCOUNT_SET_USER_HOME_BRANCH_API_PATH, {
        params: { branchCode: branchCd, userId }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error_description || error.message };
    }
  }

  static async deleteUserNotificationPreferences(customerCode, userId) {
    try {
      const response = await EpiApiClient.post(
        `${USER_NOTIFICATION_PREFERENCES_API_PATH}/delete`,
        null,
        {
          handleError: false,
          params: { customerCode, userId }
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.ModelState || error.message, Message: error.response?.data?.Message };
    }
  }

  static async deleteUserNotification(customerCode, userId) {
    try {
      const response = await EpiApiClient.post(
        DELETE_USER_NOTIFICATION_PREFERENCES_API_PATH,
        null,
        {
          handleError: false,
          params: { customerCode, userId }
        }
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.ModelState || error.message, Message: error.response?.data?.Message };
    }
  }

}

export default accountService;
